import { Logtail } from '@logtail/browser'
import posthog from 'posthog-js'

interface OriginalConsole {
  log: typeof console.log
  info: typeof console.info
  warn: typeof console.warn
  error: typeof console.error
  debug: typeof console.debug
}

let originalConsole: OriginalConsole | null = null
let isOverridden = false

/**
 * True when a console.error originates from the Logtail/Better Stack browser transport
 * itself (its POST failed → "Failed to fetch" with a stack inside @logtail/*). Forwarding
 * those back into logtail.error + posthog.captureException creates an amplification loop:
 * one offline/blocked tab produces thousands of identical events (error-analysis 2026-06-22,
 * 5K occurrences from a single user). Detect by the @logtail frames in the stack and skip
 * forwarding — the message is still printed via the original console.
 */
export const isLoggerTransportError = (args: any[]): boolean =>
  args.some((a) => {
    const stack = a instanceof Error ? a.stack : typeof a === 'string' ? a : ''
    return typeof stack === 'string' && stack.includes('@logtail')
  })

export const setupConsoleOverride = (logtail: Logtail, getLogContext?: () => object) => {
  if (typeof window === 'undefined') {
    return
  }

  // If already overridden, restore first before setting up again
  if (isOverridden) {
    restoreConsole()
  }

  // Store original console methods
  originalConsole = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
    debug: console.debug,
  }

  // Override console methods
  console.log = (...args: any[]) => {
    originalConsole!.log(...args)
    logtail.info(formatConsoleMessage(args), {
      source: 'console.log',
      ...(getLogContext ? getLogContext() : { timestamp: new Date().toISOString() }),
    })
  }

  console.info = (...args: any[]) => {
    originalConsole!.info(...args)
    logtail.info(formatConsoleMessage(args), {
      source: 'console.info',
      ...(getLogContext ? getLogContext() : { timestamp: new Date().toISOString() }),
    })
  }

  console.warn = (...args: any[]) => {
    originalConsole!.warn(...args)
    logtail.warn(formatConsoleMessage(args), {
      source: 'console.warn',
      ...(getLogContext ? getLogContext() : { timestamp: new Date().toISOString() }),
    })
  }

  console.error = (...args: any[]) => {
    originalConsole!.error(...args)
    // Break the telemetry amplification loop: never re-report the logger transport's own
    // failures back through Logtail/PostHog (error-analysis 2026-06-22).
    if (isLoggerTransportError(args)) return
    const context = getLogContext ? getLogContext() : { timestamp: new Date().toISOString() }
    // Separate string/primitive args (go into message) from plain-object args (become top-level fields)
    const stringArgs = args.filter((a) => a === null || typeof a !== 'object' || a instanceof Error)
    const objectArgs = args.filter(
      (a) => a !== null && typeof a === 'object' && !(a instanceof Error),
    )
    // Fall back to the object args (then a placeholder) so we never ship an
    // empty/contentless message to Logtail (issue #3195)
    const message =
      formatConsoleMessage(stringArgs).trim() ||
      formatConsoleMessage(objectArgs).trim() ||
      '[console.error called without a message]'
    // Merge all plain-object args as top-level Logtail fields (fetchUrl, windowUrl, traceId, spanId, etc.)
    const structuredFields = objectArgs.reduce(
      (acc, arg) => {
        const { message: _msg, ...rest } = arg
        Object.assign(acc, rest)
        return acc
      },
      {} as Record<string, any>,
    )
    logtail.error(message, { source: 'console.error', ...context, ...structuredFields })
    const errorArg = args.find((a) => a instanceof Error)
    posthog.captureException(errorArg ?? new Error(message), {
      source: 'console.error',
      ...context,
    })
  }

  console.debug = (...args: any[]) => {
    originalConsole!.debug(...args)
    logtail.debug(formatConsoleMessage(args), {
      source: 'console.debug',
      ...(getLogContext ? getLogContext() : { timestamp: new Date().toISOString() }),
    })
  }

  isOverridden = true
}

export const restoreConsole = () => {
  if (!isOverridden || !originalConsole || typeof window === 'undefined') {
    return
  }

  console.log = originalConsole.log
  console.info = originalConsole.info
  console.warn = originalConsole.warn
  console.error = originalConsole.error
  console.debug = originalConsole.debug

  isOverridden = false
}

const formatConsoleMessage = (args: any[]): string => {
  return args
    .map((arg) => {
      if (typeof arg === 'string') {
        return arg
      } else if (arg instanceof Error) {
        // JSON.stringify(Error) yields '{}' (own props are non-enumerable),
        // which shipped contentless error logs to Logtail (issue #3195).
        // The stack already starts with "<name>: <message>".
        return arg.stack || `${arg.name}: ${arg.message}`
      } else if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg, null, 2)
        } catch {
          return '[Object]'
        }
      } else {
        return String(arg)
      }
    })
    .join(' ')
}
